import React, { useMemo, useState } from 'react';
import { isNull } from 'lodash';
import OpenPreviewProductDetail from '../../../../containers/app/common/OpenPreviewProductDetail';
import OpenOrderDeliveryTracking from '../../../../containers/app/common/OpenOrderDeliveryTracking';
import SendMessageService from '../../../../containers/app/common/SendMessageService';
import OpenOrderModifyDeliveryInfo from '../../../../containers/app/common/OpenOrderModifyDeliveryInfo';
import OpenOrderItemDetail from '../../../../components/link/OpenOrderItemDetail';
import OpenOrderItemStatusChange from '../../../../containers/app/common/OpenOrderItemStatusChange';
import Anchor from '../../../../components/link/Anchor';
import OpenBrandDetail from '../../../../components/link/OpenBrandDetail';
import OpenCouponDetail from '../../../../components/link/OpenCouponDetail';
import OpenProductDetail from '../../../../components/link/OpenProductDetail';
import OpenUserDetail from '../../../../components/link/OpenUserDetail';
import InfoTable from '../../../../components/table/InfoTable';
import Button from '../../../../components/Button';
import OrderCsMemoListDrawer from '../../common/drawer/OrderCsMemoListDrawer';
import { AMONDZ_MEMBER_TYPE, DATE_FORMAT } from '../../../../constants/service/common/utilConstants';
import { COUPON_AVAILABLE_TYPE_TEXT } from '../../../../constants/service/app/couponConstants';
import {
  DELIVERY_DELAY_REASON_TYPE_TEXT,
  DELIVERY_DETAIL_STATUS_TEXT,
  ORDER_ACCEPT_MANAGER_TEXT,
  ORDER_DELIVERY_TYPE_TEXT,
  ORDER_ITEM_STATUS,
  ORDER_ITEM_STATUS_TEXT,
  ORDER_PROCESS_HISTORY_TYPE_TEXT,
  ORDER_PROCESS_MANAGER_TEXT,
  PAY_METHOD_TYPE_TEXT,
  ORDER_CANCEL_STATUS_TEXT,
  ORDER_RETURN_STATUS_TEXT,
  ORDER_TYPE_TEXT,
  ORDER_GIFT_STATUS_TEXT,
  ORDER_ITEM_CS_RECEPTION_TYPE_TEXT,
  AVAILABLE_ORDER_DETAIL_STATUS_CHECK_ORDER_ITEM_STATUS_LIST,
  ORDER_ITEM_STATUS_TO_DETAIL_STATUS_TEXT,
  ORDER_CANCEL_STATUS,
  ORDER_RETURN_STATUS,
  ORDER_EXCHANGE_STATUS,
} from '../../../../constants/service/app/orderConstants';
import { BRAND_TYPE_TEXT } from '../../../../constants/service/app/brandConstants';
import { PRODUCT_MANAGEMENT_TYPE_TEXT } from '../../../../constants/service/app/productConstants';
import { asyncSimpleModal, setDash, setPhoneFormat, setNumberWithComma } from '../../../../lib/common';
import { setCopyToClipboardIcon } from '../../../../lib/utils/commonUtils';
import { setDateFormat } from '../../../../lib/moment';
import { setOrderMessage, setOrderProcessingHistoryReason } from '../../../../lib/utils/orderutils';
import { setCouponPeriod, setDiscountBenefit } from '../../../../lib/utils/couponUtils';
import './OrderDetailInfo.scss';

const OrderDetailInfo = (props) => {
  const {
    orderInfo,
    cancelHistory,
    exchangeHistory,
    returnHistory,
    requestExchangeReturn,
    insertOrderItemCsMemoRequest,
    editOrderItemCsMemoRequest,
  } = props;
  const [showCsMemoList, setShowCsMemoList] = useState(false);

  // 주문 상태 되돌리기 상태 유효성 검사
  const validateOrderStatus = useMemo(() => {
    return (
      // 배송 준비, 배송 중, 배송 완료
      [
        ORDER_ITEM_STATUS.DELIVERY_PREPARATION,
        ORDER_ITEM_STATUS.SHIPPING,
        ORDER_ITEM_STATUS.DELIVERY_COMPLETE,
        ORDER_ITEM_STATUS.PURCHASE_CONFIRMATION,
      ].includes(orderInfo.orderItemStatus) ||
      // 최소 거부
      (orderInfo.orderItemStatus === ORDER_ITEM_STATUS.CANCELLATION &&
        cancelHistory.orderCancelStatus === ORDER_CANCEL_STATUS.CANCEL_REJECT) ||
      // 반품 거부, 판매자 사유 x
      (orderInfo.orderItemStatus === ORDER_ITEM_STATUS.RETURN &&
        returnHistory.orderReturnStatus === ORDER_RETURN_STATUS.REJECT &&
        returnHistory.acceptManager !== 0) ||
      // 교환 거부, 판매자 사유 x
      (orderInfo.orderItemStatus === ORDER_ITEM_STATUS.EXCHANGE &&
        exchangeHistory.orderExchangeStatus === ORDER_EXCHANGE_STATUS.REJECT &&
        exchangeHistory.acceptManager !== 0)
    );
  }, [orderInfo.orderItemStatus, cancelHistory, exchangeHistory, returnHistory]);

  return (
    <div className="order-detail-content order-detail-info-content">
      {/* 주문 상세 정보 + 연관된 상품 주문 정보 */}
      <div className="order-detail-section three-part">
        <div className="title">주문 상세 정보</div>

        {validateOrderStatus && (
          <OpenOrderItemStatusChange
            isDelay={!isNull(orderInfo.deliveryDelayType)}
            orderItemId={orderInfo.orderItemId}
            orderItemStatus={orderInfo.orderItemStatus}
            cancelHistory={cancelHistory}
            exchangeHistory={exchangeHistory}
            returnHistory={returnHistory}
          />
        )}

        {[ORDER_ITEM_STATUS.SHIPPING, ORDER_ITEM_STATUS.DELIVERY_COMPLETE].includes(orderInfo.orderItemStatus) && (
          <Button
            className="order-item-action-button"
            isRadius
            color="primary"
            text="교환/반품으로 변경"
            onClick={() => requestExchangeReturn()}
          />
        )}

        <InfoTable>
          <tbody>
            <tr>
              <td className="label tc">주문 번호</td>
              <td className="tc">
                {orderInfo.orderId}
                {setCopyToClipboardIcon(orderInfo.orderId)}
              </td>
            </tr>
            <tr>
              <td className="label tc">상품 주문 번호</td>
              <td className="tc">
                {orderInfo.orderItemId}
                {setCopyToClipboardIcon(orderInfo.orderItemId)}
              </td>
            </tr>
            <tr>
              <td className="label tc">상품 주문 상태</td>
              <td className="tc">{ORDER_ITEM_STATUS_TEXT[orderInfo.orderItemStatus]}</td>
            </tr>
            <tr>
              <td className="label tc">주문 예시 메시지</td>
              <td className="tc">{setDash(orderInfo.orderExampleMessage)}</td>
            </tr>
            <tr>
              <td className="label tc">주문 메시지</td>
              <td className="tc">{setOrderMessage(orderInfo.orderMessage)}</td>
            </tr>
            <tr>
              <td className="label tc">유저 식별 ID</td>
              <td className="tc">
                <OpenUserDetail text={orderInfo.userId} userId={orderInfo.userId} />
              </td>
            </tr>
            <tr>
              <td className="label tc">구매자 ID</td>
              <td className="tc">
                <OpenUserDetail text={orderInfo.buyerAccount} userId={orderInfo.userId} />
              </td>
            </tr>
            <tr>
              <td className="label tc">구매자명</td>
              <td className="tc">{orderInfo.buyerName}</td>
            </tr>
            <tr>
              <td className="label tc">구매자 연락처</td>
              <td className="tc">
                <SendMessageService
                  text={setPhoneFormat(orderInfo.buyerPhone)}
                  memberType={AMONDZ_MEMBER_TYPE.USER}
                  targetId={orderInfo.userId}
                />
              </td>
            </tr>
            <tr>
              <td className="label tc">결제 수단</td>
              <td className="tc">{PAY_METHOD_TYPE_TEXT[orderInfo.payMethod]}</td>
            </tr>
            <tr>
              <td className="label tc">주문 타입</td>
              <td className="tc">{ORDER_TYPE_TEXT[orderInfo.orderType]}</td>
            </tr>
            <tr>
              <td className="label tc">선물 상태</td>
              <td className="tc">{setDash(ORDER_GIFT_STATUS_TEXT[orderInfo.giftStatus])}</td>
            </tr>
          </tbody>
        </InfoTable>

        {
          // 단일 주문 상품이면 영역을 보여주지않는다.
          orderInfo.associateOrderInfoList.length > 0 ? (
            <React.Fragment>
              <div className="title">연관된 상품 주문</div>

              <div className="associate-order-item-info-table">
                <InfoTable className="associate-order-item-info" scroll={{ x: true }}>
                  <thead>
                    <tr>
                      <th>연관된 상품 주문 번호</th>
                      <th>브랜드명</th>
                      <th>상품 주문 상태</th>
                      <th>할당된 결제 금액</th>
                      <th>엮인 환불금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderInfo.associateOrderInfoList.map((item) => (
                      <tr key={item.orderItemId}>
                        <td className="tc">
                          <OpenOrderItemDetail text={item.orderItemId} orderItemId={item.orderItemId} />
                        </td>
                        <td className="tc">{item.brandName}</td>
                        {item.orderReturnStatus ? (
                          <td className="tc">{`${ORDER_ITEM_STATUS_TEXT[item.orderItemStatus]}(${
                            ORDER_RETURN_STATUS_TEXT[item.orderReturnStatus]
                          })`}</td>
                        ) : item.orderCancelStatus ? (
                          <td className="tc">{`${ORDER_ITEM_STATUS_TEXT[item.orderItemStatus]}(${
                            ORDER_CANCEL_STATUS_TEXT[item.orderCancelStatus]
                          })`}</td>
                        ) : (
                          <td className="tc">{ORDER_ITEM_STATUS_TEXT[item.orderItemStatus]}</td>
                        )}
                        <td className="tc">{setNumberWithComma(item.totalPrice, '원')}</td>
                        <td className="tc">{setNumberWithComma(item.refundActualAmount, '원')}</td>
                      </tr>
                    ))}
                  </tbody>
                </InfoTable>
              </div>
            </React.Fragment>
          ) : null
        }
      </div>

      {/* 배송지 정보 */}
      <div className="order-detail-section three-part">
        <div className="title">배송 정보</div>

        <InfoTable>
          <tbody>
            <tr>
              <td className="label tc">수취인명</td>
              <td className="tc">{orderInfo.recipientName}</td>
            </tr>
            <tr>
              <td className="label tc">수취인 연락처</td>
              <td className="tc">
                <SendMessageService
                  text={setPhoneFormat(orderInfo.recipientPhone)}
                  memberType={AMONDZ_MEMBER_TYPE.RECIPIENT}
                  targetId={orderInfo.orderItemId}
                />
              </td>
            </tr>
            <tr>
              <td className="label tc">배송 상세 상태</td>
              <td className="tc">{setDash(DELIVERY_DETAIL_STATUS_TEXT[orderInfo.deliveryStatus])}</td>
            </tr>
            <tr>
              <td className="label tc">배송 지연</td>
              <td className="tc">
                {!isNull(orderInfo.deliveryDelayType) ? (
                  <Anchor
                    color="primary"
                    isUnderLine
                    onClick={async () =>
                      await asyncSimpleModal({
                        title: DELIVERY_DELAY_REASON_TYPE_TEXT[orderInfo.deliveryDelayType],
                        message: orderInfo.deliveryDelayReason,
                      })
                    }
                    text={DELIVERY_DELAY_REASON_TYPE_TEXT[orderInfo.deliveryDelayType]}
                  />
                ) : (
                  '-'
                )}
              </td>
            </tr>
            <tr>
              <td className="label tc">지연 발송기한</td>
              <td className="tc">{setDateFormat(orderInfo.deliveryLimitDelayDate, DATE_FORMAT.YMD_HM_DASH_FORMAT)}</td>
            </tr>
            <tr>
              <td className="label tc">배송지</td>
              <td className="tc">
                ({orderInfo.postCode})
                <br />
                {orderInfo.address}
              </td>
            </tr>
            <tr>
              <td className="label tc">배송 메세지</td>
              <td className="tc">{setDash(orderInfo.message)}</td>
            </tr>
            <tr>
              <td className="label tc">택배사</td>
              <td className="tc">{setDash(orderInfo.deliveryEnterprise)}</td>
            </tr>
            <tr>
              <td className="label tc">송장번호</td>
              <td className="tc">
                <OpenOrderDeliveryTracking text={orderInfo.invoiceNumber} deliveryId={orderInfo.deliveryId} />
              </td>
            </tr>
            <tr>
              <td className="label tc">배송 방법</td>
              <td className="tc">{setDash(ORDER_DELIVERY_TYPE_TEXT[orderInfo.deliveryType])}</td>
            </tr>
            <tr>
              <td className="label tc">배송지 변경 이력</td>
              <td className="tc">
                {!isNull(orderInfo.modifyId) ? (
                  <OpenOrderModifyDeliveryInfo orderItemId={orderInfo.orderItemId} />
                ) : (
                  setDash(orderInfo.modifyId)
                )}
              </td>
            </tr>
          </tbody>
        </InfoTable>
      </div>

      {/* 구매 상품 정보 */}
      <div className="order-detail-section three-part">
        <div className="title">구매 상품 정보</div>

        <InfoTable>
          <tbody>
            <tr>
              <td className="label tc" colSpan={2}>
                브랜드명
              </td>
              <td className="tc" colSpan={2}>
                <OpenBrandDetail text={`${orderInfo.storeName}(${orderInfo.storeId})`} storeId={orderInfo.storeId} />
              </td>
            </tr>
            <tr>
              <td className="label tc" colSpan={2}>
                브랜드 연락처
              </td>
              <td className="tc" colSpan={2}>
                {setPhoneFormat(orderInfo.storePhone)}
              </td>
            </tr>
            <tr>
              <td className="label tc" colSpan={2}>
                판매자명
              </td>
              <td className="tc" colSpan={2}>
                {`${orderInfo.sellerName}(${orderInfo.sellerId})`}
              </td>
            </tr>
            <tr>
              <td className="label tc" colSpan={2}>
                판매자 연락처
              </td>
              <td className="tc" colSpan={2}>
                <SendMessageService
                  text={setPhoneFormat(orderInfo.sellerPhone)}
                  memberType={AMONDZ_MEMBER_TYPE.SELLER}
                  targetId={orderInfo.storeId}
                />
              </td>
            </tr>
            <tr>
              <td className="label tc" colSpan={2}>
                상품명
              </td>
              <td className="tc" colSpan={2}>
                <OpenPreviewProductDetail text={orderInfo.productName} productId={orderInfo.productId} />
              </td>
            </tr>
            <tr>
              <td className="label tc" colSpan={2}>
                상품번호
              </td>
              <td className="tc" colSpan={2}>
                <OpenProductDetail text={orderInfo.productId} productId={orderInfo.productId} />
              </td>
            </tr>
            <tr>
              <td className="label tc" colSpan={2}>
                옵션정보
              </td>
              <td className="tc" colSpan={2}>
                {setDash(orderInfo.productOptionName)}
              </td>
            </tr>
            <tr>
              <td className="label tc" colSpan={2}>
                관리처
              </td>
              <td className="tc" colSpan={2}>
                {setDash(BRAND_TYPE_TEXT[orderInfo.management])}
              </td>
            </tr>
            <tr>
              <td className="label tc" colSpan={2}>
                타입
              </td>
              <td className="tc" colSpan={2}>
                {setDash(PRODUCT_MANAGEMENT_TYPE_TEXT[orderInfo.productManagementType])}
              </td>
            </tr>
          </tbody>
        </InfoTable>
      </div>

      {/* 결제 금액 정보 */}
      <div className="order-detail-section">
        <div className="title">결제 금액 정보</div>

        <InfoTable>
          <tbody>
            <tr>
              <td className="label tc">판매가 (정가)</td>
              <td className="tc">{setNumberWithComma(orderInfo.productOriginalPrice, '원')}</td>
            </tr>
            <tr>
              <td className="label tc">기간 할인</td>
              <td className="tc">{setNumberWithComma(orderInfo.productDiscountPrice, '원')}</td>
            </tr>
            <tr>
              <td className="label tc">옵션 가</td>
              <td className="tc">{setNumberWithComma(orderInfo.productOptionPrice, '원')}</td>
            </tr>
            <tr>
              <td className="label tc">수량</td>
              <td className="tc">{setNumberWithComma(orderInfo.quantity, '개')}</td>
            </tr>
            <tr>
              <td className="label tc salePrice">총 판매가</td>
              <td className="tc salePrice">
                {setNumberWithComma(
                  (orderInfo.productOriginalPrice - orderInfo.productDiscountPrice + orderInfo.productOptionPrice) *
                    orderInfo.quantity,
                  '원',
                )}
              </td>
            </tr>
            <tr>
              <td className="label tc">쿠폰 사용</td>
              <td className="tc">{setNumberWithComma(-orderInfo.usedCoupon, '원')}</td>
            </tr>
            <tr>
              <td className="label tc">포인트 사용</td>
              <td className="tc">{setNumberWithComma(-orderInfo.usedPoint, '원')}</td>
            </tr>
            <tr>
              <td className="label tc salePrice">총 결제금액</td>
              <td className="tc salePrice">{setNumberWithComma(orderInfo.totalPrice, '원')}</td>
            </tr>
            <tr>
              <td className="label tc">추가 배송비</td>
              <td className="tc">{setNumberWithComma(orderInfo.deliveryExtraFee, '원')}</td>
            </tr>
          </tbody>
        </InfoTable>
      </div>

      {orderInfo.couponId ? (
        /* 사용한 쿠폰 정보 */
        <div className="order-detail-section">
          <div className="title">사용한 쿠폰 정보</div>

          <InfoTable>
            <tbody>
              <tr>
                <td className="label tc" colSpan={2}>
                  쿠폰 ID
                </td>
                <td className="tc" colSpan={2}>
                  <OpenCouponDetail text={orderInfo.couponId} couponId={orderInfo.couponId} />
                </td>
              </tr>
              <tr>
                <td className="label tc" colSpan={2}>
                  쿠폰명
                </td>
                <td className="tc" colSpan={2}>
                  {setDash(orderInfo.couponName)}
                </td>
              </tr>
              <tr>
                <td className="label tc" colSpan={2}>
                  쿠폰 설명
                </td>
                <td className="tc" colSpan={2}>
                  {setDash(orderInfo.couponDescription)}
                </td>
              </tr>
              <tr>
                <td className="label tc" colSpan={2}>
                  메모
                </td>
                <td className="tc" colSpan={2}>
                  {setDash(orderInfo.couponMemo)}
                </td>
              </tr>
              <tr>
                <td className="label tc" colSpan={2}>
                  쿠폰 유형
                </td>
                <td className="tc" colSpan={2}>
                  {COUPON_AVAILABLE_TYPE_TEXT[orderInfo.couponAvailableType]}
                </td>
              </tr>
              <tr>
                <td className="label tc" colSpan={2}>
                  할인 금액
                </td>
                <td className="tc" colSpan={2}>
                  {setDiscountBenefit(
                    orderInfo.couponDiscountType,
                    orderInfo.couponDiscountAmount,
                    orderInfo.couponMinimumPurchasePrice,
                    orderInfo.couponMaxDiscountPrice,
                  )}
                </td>
              </tr>
              <tr>
                <td className="label tc" colSpan={2}>
                  쿠폰 유효기간
                </td>
                <td className="tc" colSpan={2}>
                  {setCouponPeriod(orderInfo.couponPeriodType, orderInfo.couponPeriod, orderInfo.couponDayPlus)}
                </td>
              </tr>
              <tr>
                <td className="label tc" colSpan={2}>
                  실제 쿠폰 유효기간
                </td>
                <td className="tc" colSpan={2}>
                  {orderInfo.couponGeneratedPeriod.startDate
                    ? setDateFormat(orderInfo.couponGeneratedPeriod.startDate, DATE_FORMAT.YMD_HM_DASH_FORMAT) +
                      ' ~ ' +
                      setDateFormat(orderInfo.couponGeneratedPeriod.endDate, DATE_FORMAT.YMD_HM_DASH_FORMAT)
                    : '-'}
                </td>
              </tr>
              <tr>
                <td className="label tc" colSpan={2}>
                  발급 일시
                </td>
                <td className="tc" colSpan={2}>
                  {setDateFormat(orderInfo.couponGeneratedDate, DATE_FORMAT.YMD_HM_DASH_FORMAT)}
                </td>
              </tr>
            </tbody>
          </InfoTable>
        </div>
      ) : (
        <div className="order-detail-section break"></div>
      )}

      {/* 주문 처리 이력 */}
      <div className="order-detail-section">
        <div className="title">주문 처리 이력</div>

        <div className="order-processing-history-table">
          <InfoTable className="order-processing-history">
            <thead>
              <tr>
                <th>상태</th>
                <th>접수자</th>
                <th>처리자</th>
                <th>처리일시</th>
                <th>사유</th>
              </tr>
            </thead>

            <tbody>
              {orderInfo.orderProcessingHistory.map((item, index) => {
                return (
                  <tr key={index}>
                    <td className="label tc">{ORDER_PROCESS_HISTORY_TYPE_TEXT[item.status]}</td>
                    <td className="tc">{ORDER_ACCEPT_MANAGER_TEXT[item.acceptManager]}</td>
                    <td className="tc">{ORDER_PROCESS_MANAGER_TEXT[item.processManager]}</td>
                    <td className="tc">{setDateFormat(item.value, DATE_FORMAT.FULL_DASH_FORMAT)}</td>
                    <td className="tc">
                      {setOrderProcessingHistoryReason({
                        status: item.status,
                        orderReturnId: item.orderReturnId,
                        orderExchangeId: item.orderExchangeId,
                        type: item.type,
                        reason: item.reason,
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </InfoTable>
        </div>
      </div>

      {/* CS 메모 */}
      <div className="order-detail-section cs-memo-section">
        <div className="title">CS 메모</div>

        <Button
          className="order-item-action-button"
          isRadius
          color="primary"
          text="CS 메모 내역보기"
          onClick={() => {
            setShowCsMemoList(!showCsMemoList);
          }}
        />

        <InfoTable className="memo-table">
          <thead>
            <tr>
              <th className="write-date">처리일시</th>
              <th className="reception-type">접수처</th>
              <th>
                <div>처리당시</div>
                <div>상품 주문 상태</div>
              </th>
              <th>처리자</th>
              <th className="link-url">관련 URL</th>
              <th className="memo">메모</th>
            </tr>
          </thead>
          <tbody>
            {orderInfo &&
              orderInfo.orderItemCsList &&
              orderInfo.orderItemCsList.map((item) => (
                <tr key={item.id}>
                  <td className="tc">{setDateFormat(item.createDate, DATE_FORMAT.FULL_DASH_FORMAT)}</td>
                  <td className="tc">{ORDER_ITEM_CS_RECEPTION_TYPE_TEXT[item.receptionType]}</td>
                  <td className="tc">
                    <div>{ORDER_ITEM_STATUS_TEXT[item.orderItemStatus]}</div>
                    {AVAILABLE_ORDER_DETAIL_STATUS_CHECK_ORDER_ITEM_STATUS_LIST.includes(item.orderItemStatus) ? (
                      <div>
                        ({setDash(ORDER_ITEM_STATUS_TO_DETAIL_STATUS_TEXT[item.orderItemStatus][item.detailStatus])})
                      </div>
                    ) : null}
                  </td>
                  <td className="tc">
                    {item.adminName}({item.adminId})
                  </td>
                  <td className="tc link-url">
                    {item.linkUrl ? (
                      <Anchor
                        isUnderLine={!!item.linkUrl}
                        color="primary"
                        text="이동하기"
                        href={item.linkUrl}
                        target="_blank"
                      />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="tc">
                    <div className="memo">
                      <div className="content">
                        <Anchor
                          color="primary"
                          isUnderLine
                          text={item.memo}
                          onClick={async () =>
                            await asyncSimpleModal({
                              className: 'cs-memo-detail-simple-modal',
                              title: 'CS 메모 상세',
                              message: item.memo,
                            })
                          }
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </InfoTable>
      </div>

      {/* CS 메모 리스트 drawer */}
      {showCsMemoList ? (
        <OrderCsMemoListDrawer
          orderItemCsList={orderInfo.orderItemCsList}
          orderItemId={orderInfo.orderItemId}
          onClose={() => setShowCsMemoList(false)}
          insertOrderItemCsMemoRequest={insertOrderItemCsMemoRequest}
          editOrderItemCsMemoRequest={editOrderItemCsMemoRequest}
        />
      ) : null}
    </div>
  );
};

export default OrderDetailInfo;
